import React, { useState, useEffect } from 'react';
import { getFormations } from '../../../services/formation.service';
import { getCenters } from '../../../services/center.service';
import cn from 'classnames';
import Card from '../../../components/Card';
import { EditorState, ContentState } from 'draft-js';
import TextInput from '../../../components/TextInput';
import Dropdown from '../../../components/Dropdown';
import Switch from '../../../components/Switch';
import Editor from '../../../components/Editor';
import styles from './StudyInformations.module.sass';

const SOURCE_OPTIONS = ['Jobboards', 'Réseaux sociaux', 'Portes ouvertes', 'Site Web', 'Partenaires', 'Recommandations', 'Salon', 'Autre'];

const SITUATION_OPTIONS = [
    { value: '1', title: '1 - Scolaire' },
    { value: '2', title: '2 - Prépa apprentissage' },
    { value: '3', title: '3 - Etudiant' },
    { value: '4', title: '4 - Contrat d\'apprentissage' },
    { value: '5', title: '5 - Contrat de professionnalisation' },
    { value: '6', title: '6 - Contrat aidé' },
    { value: '7', title: '7 - En formation au CFA avant signature d\'un contrat d\'apprentissage (L6222-12-1 du code du travail)' },
    { value: '8', title: '8 - En formation, au CFA, sans contrat, suite à rupture (5° de L6231-2 du code du travail)' },
    { value: '9', title: '9 - Stagiaire de la formation professionnelle' },
    { value: '10', title: '10 - Salarié' },
    { value: '11', title: '11 - Personne à la recherche d\'un emploi (inscrite ou non au Pôle Emploi)' },
    { value: '12', title: '12 - Inactif' }
];

const HIGHEST_DIPLOMA_OPTIONS = [
    "13 - Aucun diplôme ni titre professionnel",
    "25 - Diplôme national du brevet (DNB)",
    "26 - Certificat de formation générale",
    "38 - Autre diplôme ou titre de niveau CAP/BEP",
    "35 - Mention complémentaire",
    "34 - BEP",
    "33 - CAP",
    "49 - Autre diplôme ou titre de niveau bac",
    "43 - Baccalauréat Technologique",
    "42 - Baccalauréat Général",
    "41 - Baccalauréat Professionnel",
    "58 - Autre diplôme ou titre de niveau bac+2",
    "55 - Diplôme Universitaire de technologie",
    "54 - Brevet de Techniciens supérieurs",
    "69 - Autre diplôme ou titre de niveau bac +3 ou 4",
    "64 - Bachelor Universitaire  ed technologie BUT",
    "63 - Licence générale",
    "62 - Licence professionnelle",
    "61 - 1ère année de Master",
    "79 - Autre diplôme ou titre de niveau bac +5 ou plus",
    "74 - Diplôme d'ingénieur, diplôme de commerce",
    "73 - Master indifférencié",
    "72 - Master recherche/DEA",
    "71 - Master professionnel/DESS",
    "80 - Doctorat"
];

const StudyInformations = ({ prospect, onProspectChange, onDropdownChange, onSwitchChange, loading }) => {
    const [formations, setFormations] = useState([]);
    const [loadingFormations, setLoadingFormations] = useState(false);
    const [centers, setCenters] = useState([]);
    const [loadingCenters, setLoadingCenters] = useState(false);
    const [content, setContent] = useState(() => {
        const remarks = typeof prospect?.remarks === 'string' ? prospect.remarks : '';
        return EditorState.createWithContent(ContentState.createFromText(remarks));
    });

    useEffect(() => {
        const fetchFormations = async () => {
            setLoadingFormations(true);
            try {
                const data = await getFormations();
                setFormations(data || []);
            } catch (error) {
                console.error("Erreur lors de la récupération des formations", error);
                setFormations([]);
            } finally {
                setLoadingFormations(false);
            }
        };

        const fetchCenters = async () => {
            setLoadingCenters(true);
            try {
                const result = await getCenters({ perPage: 100, fullData: false });
                setCenters(result.items || []);
            } catch (error) {
                console.error("Erreur lors de la récupération des centres", error);
                setCenters([]);
            } finally {
                setLoadingCenters(false);
            }
        };

        fetchFormations();
        fetchCenters();
    }, []);

    const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';
        return date.toISOString().split('T')[0];
    };



    const formationOptions = formations.map(f => ({ value: f.id, title: f.exact_title_of_qualification }));
    const centerOptions = centers.map(c => ({ value: c.id, title: c.name }));
    return (
        <Card className={styles.card} title="Études & Projet" classTitle="title-green">


            <div className={styles.group}>

                <div className={styles.field}>
                    <Dropdown
                        className={styles.dropdown}
                        label="Source"
                        name="source"
                        options={SOURCE_OPTIONS}
                        value={SOURCE_OPTIONS.find(opt => opt === prospect?.source) || ''}
                        setValue={(option) => onDropdownChange('source', option)}
                    />
                </div>


                <div className={styles.field}>
                    {loadingFormations ? (
                        <p>Chargement des formations...</p>
                    ) : (
                        <Dropdown
                            className={styles.dropdown}
                            label="Formation visée"
                            name="formation"
                            options={formationOptions}
                            value={formationOptions.find(opt => opt.value === prospect?.desired_formations) || ''}
                            setValue={(option) => onDropdownChange('desired_formations', option.value)}
                            required
                        />
                    )}
                </div>

                <TextInput
                    className={styles.field}
                    label="Date de début souhaitée"
                    name="desired_start_date"
                    type="date"
                    value={formatDateForInput(prospect?.desired_start_date)}
                    onChange={onProspectChange}
                />

                <div className={styles.field}>
                    {loadingCenters ? (
                        <div>Chargement des centres...</div>
                    ) : (
                        <Dropdown
                            className={styles.dropdown}
                            label="Campus"
                            name="campus"
                            options={centerOptions}
                            value={centerOptions.find(opt => opt.value === prospect?.campus_preference) || ''}
                            setValue={(option) => onDropdownChange('campus_preference', option.value)}
                        />
                    )}
                </div>

                <div className={styles.field}>
                    <Dropdown
                        className={styles.dropdown}
                        label="Situation actuelle"
                        name="situation_before_contract"
                        options={SITUATION_OPTIONS}
                        value={SITUATION_OPTIONS.find(opt => opt.value === prospect?.current_situation) || ''}
                        setValue={(option) => onDropdownChange('current_situation', option.value)}
                    />
                </div>

                <div className={styles.field}>
                    <Dropdown
                        className={styles.dropdown}
                        label="Niveau de diplôme"
                        name="diploma_level"
                        options={HIGHEST_DIPLOMA_OPTIONS}
                        value={HIGHEST_DIPLOMA_OPTIONS.find(opt => opt === prospect?.diploma_level) || ''}
                        setValue={(option) => onDropdownChange('diploma_level', option)}
                    />
                </div>

            </div>
            <div className={styles.divider} />

            <div className={styles.group}>
                <div className={styles.field} style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ marginLeft: '10px' }}>Disponible immédiatement</span>
                    <Switch
                        name="availability"
                        value={prospect?.availability || false}
                        onChange={(value) => onSwitchChange('availability', value)}
                        className={styles.switch}
                    />
                </div>

                <div className={styles.field} style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ marginLeft: '10px' }}>Intéressé par l'alternance :</span>
                    <Switch
                        name="interested_in_alternance"
                        value={prospect?.interested_in_alternance || false}
                        onChange={(value) => onSwitchChange('interested_in_alternance', value)}
                        className={styles.switch}
                    />
                </div>
            </div>

            {prospect?.interested_in_alternance && (
                <div className={styles.group}>
                    <div className={styles.field} style={{ display: 'flex', alignItems: 'center' }}>
                        <span style={{ marginLeft: '10px' }}>A des pistes d'alternance :</span>
                        <Switch
                            name="has_alternance_leads"
                            value={prospect?.has_alternance_leads || false}
                            onChange={(value) => onSwitchChange('has_alternance_leads', value)}
                            className={styles.switch}
                        />
                    </div>
                    {prospect?.has_alternance_leads && (
                        <TextInput
                            className={styles.field}
                            label="Détails des pistes"
                            name="alternance_leads_details"
                            type="text"
                            placeholder="Précisez les pistes d'alternance"
                            value={prospect?.alternance_leads_details || ''}
                            onChange={onProspectChange}
                        />
                    )}
                </div>
            )}



            <div className={styles.divider} />

            <Editor
                state={content}
                onChange={setContent}
                classEditor={styles.editor}
                label="Remarque(s)"
                tooltip="Informations complémentaires sur le prospect"
                placeholder="Ajoutez vos remarques ici..."
            />


            <div className={styles.divider}></div>

            <div className={styles.actions}>
                <button
                    className={cn("button", styles.button)}
                    type="submit"
                    disabled={loading}
                >
                    {loading ? "Création en cours..." : "Créer le prospect"}
                </button>
            </div>
        </Card>
    );
};

export default StudyInformations;